import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import RegisterForm from "@/core/Forms/RegisterForm";
import { useAuth } from "@/core/components/Auth/AuthContext";
import { BUSINESS_NAME } from "@/core/constants/business";
import { RegisterFormResponse } from "@/core/Forms/RegisterForm/types";

const RegisterPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/account");
    }
  }, [isAuthenticated, navigate]);

  // Handle successful registration
  const handleRegistrationSuccess = (data: RegisterFormResponse) => {
    // Redirect to user dashboard after a brief delay
    setTimeout(() => {
      navigate("/account");
    }, 2000);
  };

  return (
    <>
      <Helmet>
        <title>Create Account | {BUSINESS_NAME}</title>
        <meta
          name="description"
          content="Create your account to unlock premium benefits, exclusive offers, and personalized recommendations at Street Lawyer Services."
        />
      </Helmet>

      <section className="relative py-20 bg-gradient-to-b from-law-black to-gray-900 text-white">
        <div className="absolute inset-0 z-0 opacity-20">
          <picture>
            <source
              srcSet="/images/street-lawyer-services-dc-dispensary-entrance.webp"
              type="image/webp"
            />
            <img
              src="/images/street-lawyer-services-dc-dispensary-entrance.jpg"
              alt="Street Lawyer Services Dispensary Background"
              className="w-full h-full object-cover"
            />
          </picture>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-2">Create Your Account</h1>
              <p className="text-gray-300">
                Join the Street Lawyer Services community and unlock exclusive
                benefits
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-xl p-8 text-gray-900">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-cannabis-green-600 mb-2">
                  Member Benefits
                </h2>
                <ul className="list-disc pl-5 space-y-1 mb-6 text-gray-700">
                  <li>Early access to new product drops</li>
                  <li>Birthday rewards and special discounts</li>
                  <li>Exclusive event invitations</li>
                  <li>Faster checkout experience</li>
                  <li>Order history and reordering</li>
                </ul>
                <div className="h-px bg-gray-200 mb-6"></div>
              </div>

              <RegisterForm
                onSuccess={handleRegistrationSuccess}
                initialData={user ? { email: user.email } : undefined}
              />

              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <p className="mb-4">
                    By creating an account, you agree to our:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <a
                        href="/terms-of-service"
                        className="text-cannabis-green-600 hover:underline"
                      >
                        Terms of Service
                      </a>
                    </li>
                    <li>
                      <a
                        href="/privacy-policy"
                        className="text-cannabis-green-600 hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-sm text-gray-400">
                Already have an account?{" "}
                <a
                  href="/login"
                  className="text-cannabis-green-400 hover:text-cannabis-green-300 underline"
                >
                  Sign in here
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RegisterPage;
